import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { type BillingTranslationKeys } from '@/types/translation';
import { alertDialog, confirmDialog } from '@/ui_v3';
const getVoucherAlertConfig = (status: string): {
  title: BillingTranslationKeys;
  body: BillingTranslationKeys;
} => {
  switch (status) {
    case 'VOUCHER_NOT_FOUND':
      return {
        title: 'voucher.notFound.title',
        body: 'voucher.notFound.body'
      };
    case 'VOUCHER_EXPIRED':
      return {
        title: 'voucher.expired.title',
        body: 'voucher.expired.body'
      };
    case 'VOUCHER_EXHAUSTED':
      return {
        title: 'voucher.exhausted.title',
        body: 'voucher.exhausted.body'
      };
    case 'VOUCHER_ALREADY_USED':
    case 'VOUCHER_ALREADY_ASSIGNED':
      return {
        title: 'voucher.alreadyUsed.title',
        body: 'voucher.alreadyUsed.body'
      };
    case 'VOUCHER_DEACTIVATED':
      return {
        title: 'voucher.deactivated.title',
        body: 'voucher.deactivated.body'
      };
    default:
      return {
        title: 'voucher.registerFailed.title',
        body: 'voucher.commonError.body'
      };
  }
};
export const useVoucherAlertModal = () => {
  const {
    t
  } = useTranslation(['billing', 'common']);
  const openVoucherAlertModal = useCallback((status: string) => {
    const config = getVoucherAlertConfig(status);
    void alertDialog.openAsync({
      header: {
        title: t(config.title)
      },
      body: t(config.body)
    });
  }, [t]);
  const openRegisterSuccessModal = useCallback(async (campaignName: string | undefined, canUseVouchers: boolean): Promise<boolean> => {
    if (!campaignName) {
      return false;
    }
    const dialogOptions = {
      header: {
        title: t('billing:voucher.registerSuccess', {
          campaign: campaignName
        })
      },
      body: canUseVouchers ? t('billing:voucher.registerSuccess.body1') : t('billing:voucher.registerSuccess.body2'),
      confirmText: canUseVouchers ? t('billing:voucher.activateNow') : t('common:global.confirm.button'),
      cancelText: canUseVouchers ? t('billing:voucher.useLater') : t('common:global.cancel.button')
    };
    if (canUseVouchers) {
      return await confirmDialog.openAsync({
        ...dialogOptions,
        footerClassName: 'flex-col-reverse'
      });
    }
    await alertDialog.openAsync(dialogOptions);
    return false;
  }, [t]);
  const openActivateConfirmModal = useCallback(async (campaignName: string, planType: string) => {
    return await confirmDialog.openAsync({
      header: {
        title: t('voucher.activateConfirm.title', {
          campaignName: campaignName,
          planType: planType
        })
      },
      body: t('voucher.activateConfirm.body'),
      confirmText: t('voucher.action.activate'),
      footerClassName: 'flex-col-reverse'
    });
  }, [t]);
  return {
    openVoucherAlertModal,
    openRegisterSuccessModal,
    openActivateConfirmModal
  };
};